import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb } from 'lucide-react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useI18n } from '@/hooks/useI18n';
import { DashboardListsService } from '@/services/dashboard-lists.service';
import { toast } from '@/hooks/useToast';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { TravelRequestBadge } from '@/lib/requests/TravelRequestBadge';
import { UserAvatar } from '@/components/ui/user-avatar';
import { DashboardTravelRequest } from '@/lib/models/dashboard';
import { formatDayDate } from '@/lib/builder/components/day-labels';

interface CreateItineraryFromRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Riceve la richiesta scelta: la pagina decide dove portare l'utente. */
  onConfirm: (request: DashboardTravelRequest) => void;
}

/**
 * Un itinerario nasce sempre da una richiesta: qui si sceglie quale, fra quelle
 * ancora aperte (`pending` o `assigned`). Le richieste non le ha in pancia la
 * dashboard — che carica solo itinerari — quindi si leggono all'apertura.
 */
export const CreateItineraryFromRequestModal = ({
  isOpen,
  onClose,
  onConfirm,
}: CreateItineraryFromRequestModalProps) => {
  const { t, language } = useI18n();
  const locale = language || 'it-IT';

  const [travelRequests, setTravelRequests] = useState<DashboardTravelRequest[]>([]);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Non è una useCallback: dipenderebbe da `t`, che useI18n ricrea a ogni
  // render, e l'effect qui sotto rifarebbe la RPC a ogni giro.
  const fetchTravelRequests = async () => {
    setIsLoading(true);
    try {
      // Solo richieste aperte (da prendere in carico o in lavorazione).
      const { rows } = await DashboardListsService.getTravelRequestsPage({ view: 'active', limit: null, offset: 0 });
      setTravelRequests(rows);
    } catch (err) {
      toast({
        title: String(t('common.error')),
        description: String(t('errors.generic')),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Si rilegge solo all'apertura: la lista serve fresca lì, non a ogni render.
  useEffect(() => {
    if (!isOpen) return;
    setSelectedRequestId(null);
    void fetchTravelRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Stesse colonne delle altre tabelle di richieste del prodotto.
  const columns: DataTableColumn<DashboardTravelRequest>[] = [
    {
      id: 'destination',
      header: t('travelRequest.tableDestination'),
      cellClassName: 'font-medium break-words',
      cell: (request) => request.destinations.join(', ') || '—',
    },
    {
      id: 'client',
      header: t('travelRequest.tableContact'),
      headClassName: 'w-[24%]',
      cell: (request) => (
        <UserAvatar
          expanded
          fullName={request.contactFullName}
          email={request.contactEmail}
          imageUrl={request.contact.avatarUrl}
          size="sm"
          showTooltip
        />
      ),
    },
    {
      id: 'dates',
      header: t('travelRequest.tableDates'),
      headClassName: 'w-[22%]',
      cell: (request) => (
        <div className="flex flex-col">
          <span>
            {formatDayDate(request.travelStartDate, locale)} – {formatDayDate(request.travelEndDate, locale)}
          </span>
          <span className="text-xs text-gray-500">
            {t('builder.durationDays', { count: request.totalDays })}
          </span>
        </div>
      ),
    },
    {
      id: 'guests',
      header: t('travelRequest.tablePeople'),
      headClassName: 'w-[10%]',
      cell: (request) => request.totalGuests || 1,
    },
    {
      id: 'status',
      header: t('travelRequest.tableStatus'),
      headClassName: 'w-[14%]',
      cell: (request) => <TravelRequestBadge travelRequest={request} />,
    },
  ];

  const handleConfirm = () => {
    const request = travelRequests.find((req) => req.id === selectedRequestId);
    if (request) onConfirm(request);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('dashboard.createItinerary')}
      size="lg"
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!selectedRequestId}>
            {t('dashboard.startBuilding')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4 p-6">
        {/* Un itinerario slegato da una richiesta non ha cliente né date: quel
            lavoro si fa meglio come template a giorni, da agganciare dopo. */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <p className="text-sm text-blue-900">{t('dashboard.noRequestHint')}</p>
          <Link
            to="/playground"
            onClick={onClose}
            className="mt-1 inline-flex items-center gap-1 text-sm font-medium text-blue-700 underline underline-offset-2 hover:text-blue-900"
          >
            <Lightbulb className="h-3.5 w-3.5" />
            {t('dashboard.noRequestHintLink')}
          </Link>
        </div>

        <Label>{t('dashboard.selectActiveRequest')}</Label>
        <div className="h-[45vh]">
          <DataTable<DashboardTravelRequest>
            data={travelRequests}
            columns={columns}
            getRowId={(request) => request.id}
            onRowClick={(request) => setSelectedRequestId(request.id)}
            selectedRowId={selectedRequestId}
            isLoading={isLoading}
            pageSize={5}
            tableClassName="min-w-[720px]"
            emptyState={
              <div className="text-center py-8 text-gray-500">
                <p>{t('dashboard.noActiveRequests')}</p>
                <p className="text-sm mt-1">{t('dashboard.noActiveRequestsDesc')}</p>
              </div>
            }
          />
        </div>
      </div>
    </Modal>
  );
};
